// src/App.js
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { store } from './store';
import { PremiumProvider } from './hooks/usePremium';
import PaywallModal from './components/PaywallModal';
import Layout from './components/layout/Layout';
import PrivateRoute from './components/auth/PrivateRoute';
import ScrollToTop from './components/common/ScrollToTop';

// Pages
import HomePage from './pages/Home/HomePage';
import LoginPage from './pages/Auth/LoginPage';
import RegisterPage from './pages/Auth/RegisterPage';
import DashboardPage from './pages/Dashboard/DashboardPage';
import ProblemsPage from './pages/Problems/ProblemsPage';
import ProblemDetailPage from './pages/Problems/ProblemDetailPage';
import CreateProblemPage from './pages/Problems/CreateProblemPage';
import SolutionsPage from './pages/Solutions/SolutionsPage';
import SolutionDetailPage from './pages/Solutions/SolutionDetailPage';
import CreateSolutionPage from './pages/Solutions/CreateSolutionPage';
import EditSolutionPage from './pages/Solutions/EditSolutionPage';
import OrganizationsPage from './pages/Organizations/OrganizationsPage';
import OrganizationDetailPage from './pages/Organizations/OrganizationDetailPage';
import CreateOrganizationPage from './pages/Organizations/CreateOrganizationPage';
import ProfilePage from './pages/Profile/ProfilePage';
import SubscriptionsPage from './pages/Subscriptions/SubscriptionsPage';
import PaymentsPage from './pages/Payments/PaymentsPage';
import PricingPage from './pages/PricingPage';
import BillingSuccessPage from './pages/Billing/BillingSuccessPage';
import ForgotPasswordPage from './pages/Auth/ForgotPasswordPage';
import PasswordResetConfirmPage from './pages/Auth/PasswordResetConfirmPage';

// Legal Pages
import PrivacyPage from './pages/Legal/PrivacyPage';
import TermsPage from './pages/Legal/TermsPage';
import SecurityPage from './pages/Legal/SecurityPage';
import CookiesPage from './pages/Legal/CookiesPage';

// Resource Pages
import HelpCenterPage from './pages/Resources/HelpCenterPage';
import DocumentationPage from './pages/Resources/DocumentationPage';
import ApiPage from './pages/Resources/ApiPage';
import StatusPage from './pages/Resources/StatusPage';

// Company Pages
import AboutPage from './pages/Company/AboutPage';
import CareersPage from './pages/Company/CareersPage';
import BlogPage from './pages/Company/BlogPage';
import ContactPage from './pages/Company/ContactPage';

import GlobalStyles from './components/common/GlobalStyles';
import { useAppDispatch } from './store/hooks';
import { fetchProfile } from './store/slices/authSlice';
import apiService from './services/api';

const rengigsColors = {
  primary: '#2E7D32',
  secondary: '#EC4899',
  accent1: '#F59E0B',
  accent2: '#10B981',
  accent3: '#3B82F6',
  dark: '#1F2937',
  light: '#F9FAFB',
};

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: rengigsColors.primary, light: '#4CAF50', dark: '#1B5E20', contrastText: '#ffffff' },
    secondary: { main: rengigsColors.secondary, light: '#F472B6', dark: '#DB2777', contrastText: '#ffffff' },
    success: { main: rengigsColors.accent2, light: '#34D399', dark: '#059669' },
    warning: { main: rengigsColors.accent1, light: '#FBBF24', dark: '#D97706' },
    error: { main: '#EF4444', light: '#F87171', dark: '#DC2626' },
    info: { main: rengigsColors.accent3, light: '#60A5FA', dark: '#2563EB' },
    background: { default: '#F8F9FA', paper: '#FFFFFF' },
    text: { primary: '#111827', secondary: '#6B7280' },
  },
  typography: {
    fontFamily: '"Inter", "Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: { fontSize: 'clamp(2.5rem, 5vw, 4rem)', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.2 },
    h2: { fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 700, letterSpacing: '-0.01em', lineHeight: 1.3 },
    h3: { fontSize: 'clamp(1.5rem, 3vw, 2rem)', fontWeight: 600, lineHeight: 1.4 },
    h4: { fontSize: 'clamp(1.25rem, 2.5vw, 1.5rem)', fontWeight: 600, lineHeight: 1.4 },
    h5: { fontSize: 'clamp(1.1rem, 2vw, 1.25rem)', fontWeight: 600, lineHeight: 1.5 },
    h6: { fontSize: 'clamp(1rem, 1.5vw, 1.1rem)', fontWeight: 600, lineHeight: 1.5 },
    body1: { fontSize: 'clamp(0.875rem, 1.5vw, 1rem)', lineHeight: 1.6 },
    body2: { fontSize: 'clamp(0.75rem, 1.2vw, 0.875rem)', lineHeight: 1.6 },
    button: { textTransform: 'none', fontWeight: 600, fontSize: 'clamp(0.875rem, 1.2vw, 1rem)' },
  },
  shape: { borderRadius: 16 },
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        * { scroll-behavior: smooth; }
        ::-webkit-scrollbar { width: 10px; height: 10px; }
        ::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 10px; }
        ::-webkit-scrollbar-thumb {
          background: linear-gradient(135deg, ${rengigsColors.primary} 0%, #4CAF50 100%);
          border-radius: 10px;
        }
        ::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(135deg, #4CAF50 0%, ${rengigsColors.primary} 100%);
        }
      `,
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '12px', padding: '10px 24px', boxShadow: 'none',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 10px 20px -5px rgba(46, 125, 50, 0.3)' },
        },
        containedPrimary: {
          background: `linear-gradient(135deg, ${rengigsColors.primary} 0%, #4CAF50 100%)`,
          '&:hover': { background: `linear-gradient(135deg, #4CAF50 0%, ${rengigsColors.primary} 100%)` },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '20px',
          background: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(46, 125, 50, 0.1)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-8px) scale(1.02)',
            border: '1px solid rgba(46, 125, 50, 0.3)',
            boxShadow: `0 20px 30px -10px ${rengigsColors.primary}40`,
          },
        },
      },
    },
    MuiPaper: { styleOverrides: { root: { borderRadius: '20px' } } },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '12px', transition: 'all 0.3s',
            '&:hover fieldset': { borderColor: rengigsColors.primary },
            '&.Mui-focused fieldset': { borderColor: rengigsColors.primary, borderWidth: '2px' },
          },
        },
      },
    },
  },
});

const AppContent = () => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (token) dispatch(fetchProfile());
    apiService.fetchCSRFToken();
  }, [dispatch]);

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <ScrollToTop />
      <PaywallModal />
      <Layout>
        <Routes>
          {/* Public */}
          <Route path="/" element={<HomePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/password-reset-confirm/:uid/:token" element={<PasswordResetConfirmPage />} />
          <Route path="/billing/success" element={<PrivateRoute><BillingSuccessPage /></PrivateRoute>} />

          {/* Public Listing (but redirect unauthenticated inside component) */}
          <Route path="/problems" element={<ProblemsPage />} />
          <Route path="/solutions" element={<SolutionsPage />} />
          <Route path="/organizations" element={<OrganizationsPage />} />

          {/* Protected Details */}
          <Route path="/problems/:id" element={<PrivateRoute><ProblemDetailPage /></PrivateRoute>} />
          <Route path="/solutions/:id" element={<PrivateRoute><SolutionDetailPage /></PrivateRoute>} />
          <Route path="/solutions/:id/edit" element={<PrivateRoute><EditSolutionPage /></PrivateRoute>} />
          <Route path="/organizations/:id" element={<PrivateRoute><OrganizationDetailPage /></PrivateRoute>} />

          {/* Protected Create */}
          <Route path="/problems/create" element={<PrivateRoute><CreateProblemPage /></PrivateRoute>} />
          <Route path="/solutions/create" element={<PrivateRoute><CreateSolutionPage /></PrivateRoute>} />
          <Route path="/organizations/create" element={<PrivateRoute><CreateOrganizationPage /></PrivateRoute>} />

          {/* Protected User */}
          <Route path="/dashboard" element={<PrivateRoute><DashboardPage /></PrivateRoute>} />
          <Route path="/profile" element={<PrivateRoute><ProfilePage /></PrivateRoute>} />
          <Route path="/subscriptions" element={<PrivateRoute><SubscriptionsPage /></PrivateRoute>} />
          <Route path="/payments" element={<PrivateRoute><PaymentsPage /></PrivateRoute>} />

          {/* Legal Pages (public) */}
          <Route path="/legal/privacy" element={<PrivacyPage />} />
          <Route path="/legal/terms" element={<TermsPage />} />
          <Route path="/legal/security" element={<SecurityPage />} />
          <Route path="/legal/cookies" element={<CookiesPage />} />

          {/* Resource Pages (public) */}
          <Route path="/help" element={<HelpCenterPage />} />
          <Route path="/docs" element={<DocumentationPage />} />
          <Route path="/api" element={<ApiPage />} />
          <Route path="/status" element={<StatusPage />} />

          {/* Company Pages (public) */}
          <Route path="/about" element={<AboutPage />} />
          <Route path="/careers" element={<CareersPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/contact" element={<ContactPage />} />

          {/* Catch-all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Layout>
    </Router>
  );
};

function App() {
  return (
    <Provider store={store}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <GlobalStyles />
        <PremiumProvider>
          <AppContent />
          <ToastContainer
            position="top-right"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop
            closeOnClick
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="colored"
          />
        </PremiumProvider>
      </ThemeProvider>
    </Provider>
  );
}

export default App;